import {ComponentProps, useState} from "react";
import {ClientType} from "@/api";
import {MultipleLineError, DialogContent, FormattedDialogTitle, EnumField} from "@dvrt/graphql-widgets";
import {FormattedFormRow, FormContainer, FormDialogActionButtons} from "@dvrt/graphql-widgets";
import {Dialog, DialogActions, TextField} from "@mui/material";
import {useForm} from "@/utils/Form";
import ErrorHelper from "../../../../../utils/ErrorHelper";
import CreateAnotherCheckBox from "../../../../CreateAnotherCheckBox";
import {FormattedMessage, MessageDescriptor, defineMessage} from "react-intl";
import {SaveClientDeviceMutationVariables, useSaveClientDeviceMutation} from "../../queries.generated";
import {CLIENT_TYPE_MESSAGES} from "@/utils/translations";

type Props = {
    isOpen: boolean;
    close: () => void;
    onSuccess: () => void;
    title: MessageDescriptor;
    initialData: SaveClientDeviceMutationVariables;
    cacheKeyPostfix: string;
    titleVariables?: ComponentProps<typeof FormattedDialogTitle>["values"];
};

export default ({isOpen, close, title, initialData, onSuccess, cacheKeyPostfix, titleVariables}: Props) => {
    const [createAnother, setCreateAnother] = useState(false);
    const [success, setSuccess] = useState(false);

    const form = useForm(useSaveClientDeviceMutation, "saveClientDevice", initialData, null, null, cacheKeyPostfix);

    const errorHelper = new ErrorHelper(form.errors);

    async function submit() {
        if (!(await form.submit())) {
            return;
        }

        setSuccess(true);
        form.reset();

        if (!createAnother) handleClose(true);
    }

    function handleClose(asyncSuccess?: boolean) {
        const actualSuccess = asyncSuccess === null ? success : asyncSuccess;

        close();
        if (actualSuccess) onSuccess();
        form.reset();
    }

    return (
        <Dialog open={isOpen}>
            <FormattedDialogTitle title={title} values={titleVariables}>
                {errorHelper.hasError("limit") && (
                    <p style={{color: "red"}}>
                        <FormattedMessage id="quota_depleted" defaultMessage="Quota depleted" />
                    </p>
                )}
            </FormattedDialogTitle>
            <DialogContent>
                <FormContainer>
                    <FormattedFormRow label={defineMessage({id: "description", defaultMessage: "Description"})}>
                        <TextField
                            variant="standard"
                            id="client-device-description"
                            margin="dense"
                            value={form.values.description}
                            onChange={(event) => form.updateValues({description: event.target.value})}
                            error={errorHelper.hasError("description")}
                            helperText={<MultipleLineError errors={errorHelper.fieldErrors("description")} />}
                        />
                    </FormattedFormRow>
                    {form.values.id && (
                        <FormattedFormRow label={defineMessage({id: "type", defaultMessage: "Type"})}>
                            <EnumField
                                value={form.values.clientType}
                                enum_={{ClientType}}
                                onChange={(clientType) => form.updateValues({clientType})}
                                filter={(item) => item !== ClientType.All}
                                translations={CLIENT_TYPE_MESSAGES}
                            />
                        </FormattedFormRow>
                    )}
                </FormContainer>
            </DialogContent>
            <DialogActions>
                {form.values.userId && <CreateAnotherCheckBox state={createAnother} setState={setCreateAnother} />}
                <FormDialogActionButtons
                    mainId="client-device-save"
                    mainAction={submit}
                    secondaryAction={() => form.reset()}
                    cancel={handleClose}
                    enabled={form.hasChanged}
                />
            </DialogActions>
        </Dialog>
    );
};
